<template>
    <b-modal :id="modalId" :title="title" hide-footer size="lg" @show="show">
        <b-alert v-if="prescriber && !prescriber.faxNumber" show variant="danger">
            Prescriber doesn't have any fax number.
        </b-alert>
        <b-row>
            <b-col v-if="formType == formTypeEnum.AlternateRxRequest" cols="6">
                <b>FROM: </b><span>{{ store.name }}</span><br>
                <span>{{ store.fullAddress }}</span><br>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="6">
                <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                <span>{{ patient.fullAddress }}</span><br>
                <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatLocalDate('L') }}</span>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <b>PHONE: </b><span>{{ patient.phoneNumber | formatPhone }}</span><br>
            </b-col>
            <b-col cols="4">
                <b>CELL PHONE: </b>{{ patient.cellNumber | formatPhone }}<br>
            </b-col>
        </b-row>
        <hr>
        <PrescriptionDetailsSummary v-if="prescription && formType == formTypeEnum.AlternateRxRequest"
            v-model="prescription" />
        <hr>
        <b>Alternate Medications: </b>
        <b-table v-if="alternateMedications.length > 0" :fields="alternateMedicationFields" :items="alternateMedications">
            <template #cell(actions)="row">
                <b-button size="sm" variant="danger" @click="removeAlternateDrug(row.index)">Remove</b-button>
            </template>
        </b-table>
        <b-row align-v="end">
            <b-col>
                <SearchComponent v-if="formType == formTypeEnum.AlternateRxRequest" v-model="selectedDrug"
                    :label="`Drug`" :search-u-r-l="`/Drug`" :required="true"></SearchComponent>
            </b-col>
            <b-col cols="auto">
                <b-button size="sm" variant="primary" @click="addAlternateDrug">Add Drug</b-button>
            </b-col>
        </b-row>

        <hr>

        <b-row>
            <b-col>
                <fg-input label="PHARMACY COMMENTS">
                    <AutoCompleteTextarea v-model="faxFormData.note" class="textarea-autosize" block-listener
                        placeholder="Write here any comments" />
                </fg-input>
            </b-col>
        </b-row>

        <b-row v-if="prescriber && prescriber.altFaxNumber">
            <b-col>
                <b-check v-model="useAltFax">
                    Use Alternate Fax Number ({{ prescriber.altFaxNumber }})
                </b-check>
            </b-col>
        </b-row>

        <hr>

        <b-row v-if="isSending">
            <b-spinner class="mr-2" label="Spinning"></b-spinner>
            <strong>Sending...</strong>
        </b-row>
        <b-row v-else>
            <b-col>
                <b-button block class="mt-3" @click="close">Cancel</b-button>
            </b-col>
            <b-col>
                <b-button :disabled="!isFormValid" block class="mt-3" variant="success" @click="send()">Send</b-button>
            </b-col>
        </b-row>

    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop } from "vue-property-decorator";
import { Store } from '@/models/Store';
import { Prescriber } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { FaxForm, Prescription } from '@/models/Prescription';
import { PatientDocument } from "@/models/PatientDocument";
import { FaxType } from "@/models/PrescriberFollowUpCase";
import { Drug } from "@/models/Drug/Drug";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue'
import SearchComponent from '@/components/SearchComponent.vue';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';

@Component({
    name: "AlternateRxFormModal",
    components: { PrescriptionDetailsSummary, SearchComponent, AutoCompleteTextarea }
})
export default class AlternateRxFormModal extends Vue {
    @Prop({ default: () => new Store() }) private store!: Store;
    @Prop({ default: () => new Prescriber() }) private prescriber!: Prescriber;
    @Prop() private prescription!: Prescription;
    @Prop() private patient!: Patient;
    @Prop({ default: () => new Drug() }) private drug!: Drug;
    @Prop({ default: "" }) private modalId!: string;
    @Prop({ default: FaxType.RefillAuthorization }) private formType!: FaxType;

    private faxFormData: FaxForm = new FaxForm();
    private isSending: boolean = false;

    private selectedDrug: Drug = new Drug();
    private alternateMedications: Drug[] = [];

    private formTypeEnum = FaxType;

    protected useAltFax = false;

    get title(): string {
        if (this.formType == FaxType.AlternateRxRequest) return 'Alternate Prescription Request';

        return '';
    }

    get alternateMedicationFields() {
        return [
            { key: "ndc", label: "NDC" },
            { key: "productNameShort", label: "Drug Name" },
            { key: "actions", label: "", class: "text-right" },
        ];
    }

    get endpointURI(): string {
        let res = '';
        if (this.formType == FaxType.AlternateRxRequest) res = 'alternate-rx-request';

        return res;
    }

    get prescriberToShow(): Prescriber {
        return this.prescriber;
    }

    get drugToShow(): Drug {
        return this.drug?.packageID ? this.drug : this.selectedDrug;
    }

    get isFormValid(): boolean {
        const hasAlternateDrug = this.alternateMedications.length > 0
            || (this.alternateMedications.length === 0 && !!this.selectedDrug.drugId);
        const validSelectedFields = this.formType == FaxType.AlternateRxRequest && hasAlternateDrug;

        return !!this.prescriberToShow.faxNumber && validSelectedFields;
    }

    get showInvalidFormAlert(): boolean {
        return !this.isFormValid;
    }

    addAlternateDrug() {
        if (this.selectedDrug.drugId && this.alternateMedications.length < 3) {
            this.alternateMedications.push(this.selectedDrug);
            this.selectedDrug = new Drug();
        }
    }

    removeAlternateDrug(index: number) {
        this.alternateMedications.splice(index, 1);
    }

    show() {
    }

    send() {
        let errorMsg = `Error while sending ${this.title}.`;

        // Auto-add selected drug only if no drugs were manually added
        if (this.selectedDrug.drugId && this.alternateMedications.length === 0) {
            this.alternateMedications.push(this.selectedDrug);
            this.selectedDrug = new Drug();
        }

        this.faxFormData.fax = this.useAltFax && this.prescriberToShow.altFaxNumber ?
            this.prescriberToShow.altFaxNumber : this.prescriberToShow.faxNumber;

        if (this.formType == FaxType.AlternateRxRequest) {
            this.faxFormData.prescriberID = Number(this.prescriber.id);
            this.faxFormData.patientID = Number(this.patient.id);
            this.faxFormData.packageID = Number(this.prescription.packageID);
            this.faxFormData.drugSource = Number(this.prescription.drugSource);
            this.faxFormData.alternateDrug = this.alternateMedications.map(d => `${d.ndc} - ${d.productNameShort}`).join("\n");
            this.faxFormData.storeID = Number(this.store.id);
        } else {
            this.faxFormData.rxID = this.prescription.rxID
        }

            this.isSending = true;
            this.$http.post<PatientDocument>(`prescription/${this.endpointURI}`, this.faxFormData)
                .then(res => {
                    if (res.data.id) {
                        let msg = this.title + " sent successfully and saved as a new patient document. A new Follow-Up case was created.";
                        this.$notification(
                            NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success)
                        );
                        this.$emit('saved');
                        this.$bvModal.hide(this.modalId);
                    }
                })
                .catch(err => {
                    this.$notification(NotificationOptions.error(err));
                    console.error(errorMsg, { err, response: err?.response })
                })
                .finally(() => this.isSending = false)
        }

    close() {
        this.$bvModal.hide(this.modalId);
        this.selectedDrug = new Drug();
    }

    clearForm() {
        this.faxFormData = new FaxForm();
    }
}
</script>

<style scoped></style>
